import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { createStripeClient, getStripeErrorMessage, type StripeEnv } from "@/lib/stripe.server";

type Result<T> = T | { error: string };

export const startIdentityVerification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ environment: z.enum(["sandbox", "live"]) }).parse(d))
  .handler(async ({ data, context }): Promise<Result<{ clientSecret: string }>> => {
    try {
      const { userId } = context;
      const stripe = createStripeClient(data.environment as StripeEnv);
      const session = await stripe.identity.verificationSessions.create({
        type: "document",
        metadata: { userId },
        options: { document: { require_matching_selfie: true, require_live_capture: true } },
      });

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("identity_verifications").insert({
        user_id: userId,
        stripe_verification_session_id: session.id,
        status: "requires_input",
      });

      return { clientSecret: session.client_secret ?? "" };
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

// Sets / updates a bartender's home postcode + travel radius. Geocodes the
// postcode via postcodes.io (same source used for shifts) and stores
// lat/lng/postcode/travel_radius_km on the user's profile.
export const setBartenderLocation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        postcode: z.string().regex(UK_POSTCODE, { message: "Enter a valid UK postcode." }),
        travel_radius_km: z.number().int().min(1).max(200).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(data.postcode.trim())}`,
    );
    if (!res.ok) throw new Error("Postcode not found. Please check and try again.");
    const json = (await res.json()) as { result?: { latitude: number; longitude: number } };
    if (!json.result) throw new Error("Postcode not found. Please check and try again.");

    const update = {
      postcode: data.postcode.toUpperCase().replace(/\s+/g, " ").trim(),
      latitude: json.result.latitude,
      longitude: json.result.longitude,
      ...(typeof data.travel_radius_km === "number"
        ? { travel_radius_km: data.travel_radius_km }
        : {}),
    };

    const { error } = await supabase.from("profiles").update(update).eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// Backfill coordinates for a bartender whose postcode was captured at sign-up
// (stored by the account trigger) but never geocoded, because sign-up happens
// before the email is confirmed and therefore without an authenticated session.
// Safe to call on every sign-in: it no-ops when coordinates already exist.
export const ensureMyLocationGeocoded = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: prof } = await supabase
      .from("profiles")
      .select("postcode, latitude, longitude")
      .eq("id", userId)
      .maybeSingle();
    const row = prof as { postcode: string | null; latitude: number | null; longitude: number | null } | null;
    if (!row?.postcode || (row.latitude != null && row.longitude != null)) return { ok: true, updated: false };
    if (!UK_POSTCODE.test(row.postcode.trim())) return { ok: true, updated: false };

    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(row.postcode.trim())}`,
    );
    if (!res.ok) return { ok: true, updated: false };
    const json = (await res.json()) as { result?: { latitude: number; longitude: number } };
    if (!json.result) return { ok: true, updated: false };

    await supabase
      .from("profiles")
      .update({ latitude: json.result.latitude, longitude: json.result.longitude })
      .eq("id", userId);
    return { ok: true, updated: true };
  });

